import React, { useContext, useEffect } from "react"
import tw from "twin.macro"
import { Card } from "../../atoms/Card"
import { CouponProps } from "./Coupons.d"
import { Image } from "../../atoms/Image"
import appleWalletGraphic from "../../../images/appleWalletGraphic.svg"
import googleWalletGraphic from "../../../images/googleWalletGraphic.svg"
import spanishGoogleWalletGraphic from "../../../images/spanishGoogleWalletGraphic.svg"
import { ButtonLink } from "../../atoms/Button"
import Icon from "../../atoms/Icon"
import moment from "moment"
import useTealiumEvent from "../../../hooks/Tealium/useTealiumEvent"
import { LanguageContext } from "../../../contexts/Language"
import { ShareButton } from "../../atoms/ShareButton"
import { CouponsClient } from "../../../clients/CouponsClient"
import {
  setDisclaimers,
  toggleDisclaimersModal,
} from "../../../contexts/Disclaimers/actions"
import { parseDisclaimerBlocks } from "../../../helpers"
import { DisclaimersContext } from "../../../contexts/Disclaimers"
import _ from "lodash"
import { Disclaimer } from "../../../contexts/Disclaimers/disclaimers.d"
import { useTealiumContext } from "../../../contexts/Tealium"
import { css } from "@emotion/react"

/**
 * Coupon
 *
 * @component Coupon
 * @summary - Coupon card used in Coupons carousel
 *
 * @param {CouponType} coupon
 * @param {number} index - Index used to create the analytics-id used for tagging purposes
 * @param {number} parentLeft - Optional DOMRect left value for parent element. Used to reposition disclaimers to prevent popover from being outside of parent.
 * @param {number} parentRight - Optional DOMRect right value for parent element. Used to reposition disclaimers to prevent popover from being outside of parent.
 * @returns <div></div>
 */

const Coupon: React.FC<CouponProps> = ({
  coupon,
  index = 0,
  parentLeft,
  parentRight,
}) => {
  const language = useContext(LanguageContext)
  const [{ disclaimers }, dispatch] = useContext(DisclaimersContext)
  const userAgent = navigator?.userAgent
  const isAniOS = /iPad|iPhone/.test(userAgent)
  const isAndroid = /android/i.test(userAgent)
  const pillColor =
    coupon?.pillLabel == "Maintenance"
      ? "#1B1E1E"
      : coupon?.pillLabel == "Parts"
        ? "#666C61"
        : coupon?.pillLabel == "Plans"
          ? "#50697B"
          : coupon?.pillLabel == "Service"
            ? "#78493F"
            : coupon?.pillLabel == "Coupon"
              ? "#495057"
              : "#666C61"

  const getWalletPass = (couponId: string, deviceType: string) => {
    CouponsClient.getCoupon(couponId, deviceType).then(coupon => {
      //TODO what do we do with the response
      // console.log(coupon)
    })
  }

  // Add coupon's disclaimers to disclaimers context
  useEffect(() => {
    const containsAllCouponDisclaimers = coupon.disclaimers?.every(
      (disclaimer: Disclaimer) =>
        disclaimers.some(
          (existingDisclaimer: Disclaimer) =>
            existingDisclaimer.code === disclaimer.code,
        ),
    )

    if (!containsAllCouponDisclaimers) {
      const newDisclaimers = _.unionBy(coupon.disclaimers, disclaimers, "code")
      dispatch(setDisclaimers(newDisclaimers))
    }
  }, [disclaimers])

  // Tealium
  const { trackTealEvent } = useTealiumEvent()
  const { tealPageData } = useTealiumContext()

  return (
    <div
      css={[
        tw`flex-none w-10/12 mx-auto pb-24`,
        tw`md:(w-72 mx-auto min-w-[300px])`,
        tw`xl:(min-w-[310px])`,
      ]}
      id={coupon._id}
    >
      <Card
        className="coupon"
        shadowLevel={0}
        css={[
          tw`relative bg-white h-[570px] pb-6 border-2 border-dashed border-black m-3 mb-5 overflow-visible md:(p-0)`,
        ]}
      >
        <span
          css={[
            tw`absolute text-center mx-auto text-gray-300 text-xs rounded-full px-2 pb-0.5 -top-2 left-1/2 transform -translate-x-1/2`,
            css`
              background-color: ${coupon?.pillLabel ? pillColor : "#495057"};
            `,
          ]}
        >
          {coupon?.pillLabel
            ? language._(coupon?.pillLabel)
            : language._("Coupon")}
        </span>
        <ShareButton
          url={
            language?.language === "es"
              ? `/es/coupon/${coupon?._id}`
              : `/coupon/${coupon?._id}`
          }
          title={coupon?.name}
          iconStyles={[tw`block mr-0 ml-auto`]}
          css={[tw`block ml-auto mr-0 mb-1.5 -mt-1.5`]}
          print_coupon_type={coupon?.type}
          print_coupon_title={coupon?.offer}
          vehicleCategory={coupon?.vehicleCategory}
          index={index}
        />
        <div css={tw`grid gap-2 content-between h-full pb-3 pt-1.5`}>
          <Image
            imageData={coupon?.image?.image}
            css={[tw`h-32 mx-auto object-contain md:(h-32)`]}
          />
          <p css={[tw`font-semibold text-xl pt-0 text-center block`]}>
            {parseDisclaimerBlocks(
              `${coupon?.name}`,
              selection => dispatch(toggleDisclaimersModal(selection)),
              parentLeft,
              parentRight,
            )}
          </p>
          <div
            css={[
              tw`font-semibold text-red-500 text-4xl text-center md:min-h-[140px]`,
            ]}
          >
            {parseDisclaimerBlocks(
              `${coupon?.offer}`,
              selection => dispatch(toggleDisclaimersModal(selection)),
              parentLeft,
              parentRight,
            )}
          </div>
          <div
            css={[
              tw`font-book text-gray-700 text-xs text-center flex justify-center`,
            ]}
          >
            {language._("Exp")}: {moment(coupon.expires).format("M-D-YY")}
            <Icon.Warning
              color="red-400"
              css={[tw`max-h-3 mt-0.5 ml-1 inline text-red-400`]}
            />
          </div>

          {isAniOS && (
            <button
              css={[tw`m-[0 auto] hover:(cursor-pointer shadow-3 rounded-lg)`]}
              onClick={() => {
                getWalletPass(coupon?._id, "apple")
                trackTealEvent({
                  tealium_event: "add_to_wallet",
                  print_coupon_name: "Apple Wallet",
                  print_coupon_type: coupon?.type ? `${coupon?.type}` : "",
                  print_coupon_title: coupon?.offer ? `${coupon?.offer}` : "",
                  ...(coupon.vehicleCategory &&
                    coupon.vehicleCategory !== "all" && {
                      vehicle_category: [coupon.vehicleCategory],
                    }),
                })
              }}
              analytics-id={`apple wallet:coupon:${index + 1}`}
            >
              <img
                src={appleWalletGraphic}
                css={[tw`w-[110px] h-[34px]`]}
                alt="add to apple wallet"
              />
            </button>
          )}

          {isAndroid && (
            <button
              css={[
                tw`m-[0 auto] hover:(cursor-pointer shadow-3 rounded-full)`,
              ]}
              onClick={() => {
                getWalletPass(coupon?._id, "android")
                trackTealEvent({
                  tealium_event: "add_to_wallet",
                  print_coupon_name: "Google Wallet",
                  print_coupon_type: coupon?.type ? `${coupon?.type}` : "",
                  print_coupon_title: coupon?.offer ? `${coupon?.offer}` : "",
                  ...(coupon.vehicleCategory &&
                    coupon.vehicleCategory !== "all" && {
                      vehicle_category: [coupon.vehicleCategory],
                    }),
                })
              }}
              analytics-id={`Google wallet:coupon:${index + 1}`}
            >
              <img
                src={
                  language?.language === "es"
                    ? spanishGoogleWalletGraphic
                    : googleWalletGraphic
                }
                css={[language?.language === "es" ? tw`w-40` : tw`w-36`]}
                alt="add to google wallet"
              />
            </button>
          )}
        </div>
      </Card>

      <div css={[tw`flex justify-between gap-4 mx-4`]}>
        <ButtonLink
          primary
          css={[tw`text-xs px-4`]}
          to={"#dealersNearYou"}
          target="_self"
          onClick={() =>
            trackTealEvent({
              tealium_event: "lander_anchor",
              anchor_button_text: "Schedule Service",
            })
          }
          analytics-id={`schedule service:coupon:${index + 1}`}
        >
          {language._("Schedule Service")}
        </ButtonLink>

        <ButtonLink
          secondary
          css={[tw`text-xs px-4`]}
          to={`/coupon/${coupon?._id}`}
          state={{ coupon: coupon }}
          forceExternal={true} // Needed for expired coupon redirect
          target="_self" // Needed for expired coupon redirect
          onClick={() => {
            trackTealEvent({
              tealium_event: "print_coupon",
              print_coupon_name: "Print Coupon",
              print_coupon_type: coupon?.type ? `${coupon?.type}` : "",
              print_coupon_title: coupon?.offer ? `${coupon?.offer}` : "",
              ...(coupon.vehicleCategory &&
                coupon.vehicleCategory !== "all" && {
                  vehicle_category: [coupon.vehicleCategory],
                }),
            })
          }}
          analytics-id={`print coupon:coupon:${index + 1}`}
        >
          {language._("Print Coupon")}
        </ButtonLink>
      </div>
    </div>
  )
}

export default Coupon
